import { ManagedDomEventListeners } from '@ipfabric/ui-components/utils/dom/ManagedDomEventListeners';
import type { NodeId, Point } from '@ipfabric/utils';
import {
  getNodeIconRectangle,
  getNodeId,
  isGraphNodeDisplayObject,
  updateGraphNodeSelection,
} from 'components/Graph/GraphApp/GraphNode';
import { overlays } from 'components/Graph/GraphApp/helpers/overlays';
import type { GraphRendererPlugin } from 'components/Graph/GraphApp/plugins/GraphRendererPlugin';
import {
  type DeselectAllNodesCommand,
  deselectAllNodes,
} from 'components/Graph/commands/deselectAllNodes';
import { selectNodes } from 'components/Graph/commands/selectNodes';
import { record } from 'fp-ts';
import { Graphics, type interaction } from 'pixi.js';

type InteractionEvent = interaction.InteractionEvent;

const SELECTION_FRAME_NAME = 'selectionFrame';

export const nodeSelection: GraphRendererPlugin = (pluginInterface) => {
  let handleMouseMove: ((e: InteractionEvent) => void) | null = null;
  let draggingViewport = false;
  let willClick = false;

  const frame = new Graphics();
  frame.name = SELECTION_FRAME_NAME;
  frame.visible = false;
  pluginInterface.controlsLayer.addChild(frame);

  const drawSelectionFrame = (start: Point, end: Point) => {
    frame.clear();
    frame.visible = true;
    frame.beginFill(0xcccccc, 0.2);
    frame.x = Math.min(start.x, end.x);
    frame.y = Math.min(start.y, end.y);

    frame.drawRect(
      0,
      0,
      Math.max(start.x, end.x) - frame.x,
      Math.max(start.y, end.y) - frame.y,
    );
    frame.endFill();
  };

  const startSelectionUsingFrame = (startPoint: Point) => {
    handleMouseMove = (e: InteractionEvent) => {
      drawSelectionFrame(
        startPoint,
        pluginInterface.viewport.toWorld(e.data.global),
      );
      pluginInterface.requestRender();
    };
    pluginInterface.app.renderer.plugins.interaction.on(
      'mousemove',
      handleMouseMove,
    );
    pluginInterface.requestRender();
  };

  const exitSelectionUsingFrame = () => {
    if (handleMouseMove) {
      pluginInterface.app.renderer.plugins.interaction.removeListener(
        'mousemove',
        handleMouseMove,
      );
      handleMouseMove = null;
    }
    frame.visible = false;
    pluginInterface.viewport.pause = false;
  };

  const getNodeIdsInFrame = () =>
    record
      .toArray(pluginInterface.getGraphData().nodes)
      .filter(([, nodeData]) => overlays(frame, getNodeIconRectangle(nodeData)))
      .filter(
        ([nodeId]) => pluginInterface.nodeDisplayObjects.get(nodeId)?.visible,
      )
      .map(([nodeId]) => nodeId);
  const confirmSelectionUsingFrame = () => {
    if (frame.visible) {
      pluginInterface.graphDispatch(selectNodes, {
        nodeIds: getNodeIdsInFrame(),
      });
    }
    exitSelectionUsingFrame();
  };

  const updateNodeSelection = (nodeId: NodeId, selected: boolean) => {
    const node = pluginInterface.nodeDisplayObjects.get(nodeId);
    if (node) {
      updateGraphNodeSelection(node, selected);
    }
  };

  const domEventListeners = new ManagedDomEventListeners([
    window,
    'mouseup',
    () => {
      confirmSelectionUsingFrame();
      draggingViewport = false;
    },
  ]);

  pluginInterface.events.addListener('beforeUnmount', () => {
    domEventListeners.remove();
  });

  pluginInterface.viewport.on('drag-start', () => {
    draggingViewport = true;
  });

  pluginInterface.app.renderer.plugins.interaction.on(
    'mousedown',
    (event: InteractionEvent) => {
      willClick = true;
      if (pluginInterface.isSelectionMode()) {
        pluginInterface.viewport.pause = true;
        const { x, y } = pluginInterface.viewport.toWorld(event.data.global);
        startSelectionUsingFrame({ x, y });
      }
    },
  );

  pluginInterface.events.addListener('interruptClick', () => {
    willClick = false;
  });

  pluginInterface.app.renderer.plugins.interaction.on(
    'mouseup',
    (e: interaction.InteractionEvent) => {
      if (
        !draggingViewport &&
        willClick &&
        !pluginInterface.isSelectionMode() &&
        pluginInterface.getGraphData().selectedNodeIds.size > 0
      ) {
        pluginInterface.graphDispatch<DeselectAllNodesCommand | undefined>(
          deselectAllNodes,
          isGraphNodeDisplayObject(e.target)
            ? { except: new Set([getNodeId(e.target)]) }
            : undefined,
        );
      }
      willClick = false;
    },
  );

  pluginInterface.events.addListener(
    'selectionModeEnd',
    exitSelectionUsingFrame,
  );

  pluginInterface.events.addListener('graphDataRendered', (event) => {
    const { selectedNodeIds } = pluginInterface.getGraphData();
    const prevSelectedNodeIds =
      event.prevData?.selectedNodeIds ?? new Set<NodeId>();
    if (prevSelectedNodeIds === selectedNodeIds) {
      return;
    }
    // biome-ignore lint/complexity/noForEach: iterating over Set
    prevSelectedNodeIds.forEach((nodeId) => {
      if (!selectedNodeIds.has(nodeId)) {
        updateNodeSelection(nodeId, false);
      }
    });
    // biome-ignore lint/complexity/noForEach: iterating over Set
    selectedNodeIds.forEach((nodeId) => {
      if (!prevSelectedNodeIds.has(nodeId)) {
        updateNodeSelection(nodeId, true);
      }
    });
  });
};
